import { c as _c } from "react/compiler-runtime";
import { ShoppingBag } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";
import { toast } from "sonner";
import type { CartItem } from "@/api";
import { OrderDetailsModal } from "@/components/cart/order-details-modal";
import { CartPromo } from "@/components/promo/cart-promo";
import { EmptyState } from "@/components/common/states";
import { ChevronRight16Icon, InfoCircle20Icon, MinusOutlineIcon, MoreCircleIcon, PlusOutlineIcon, Share16Icon, Trash16Icon, XIcon } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { useFormatPrice } from "@/lib/use-format-price";
import { useCart, useWishlist } from "@/store";

// Товар считаем «под заказ», когда в строке корзины есть принятый кастом-оффер
// (customOfferId) или явный флаг madeToOrder. Обычные товары идут во вкладку
// «Общая». Иного признака в DTO корзины нет — если бэкенд не проставляет
// признак, вкладка «Под заказ» будет пустой (см. отчёт: зона доработки бэкенда).
const isMadeToOrder = (i: CartItem) => i.madeToOrder || Boolean(i.customOfferId);
const LOW_STOCK = 1;
type Tab = "common" | "order";
export function CartPage() {
  const {
    t
  } = useTranslation();
  const navigate = useNavigate();
  const fmt = useFormatPrice();
  const cart = useCart(s => s.cart);
  const setQty = useCart(s_0 => s_0.setQty);
  const remove = useCart(s_1 => s_1.remove);
  const removeMany = useCart(s_2 => s_2.removeMany);
  const toggleProduct = useWishlist(s_3 => s_3.toggleProduct);
  const setProductLikedOnServer = useWishlist(s_4 => s_4.setProductLikedOnServer);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState<Tab>("common");
  /*
   * Выбор товаров (Айталы 02.10.2026, A-31): при переходе в корзину выбраны
   * все товары, в том числе добавленные позже. Поэтому храним не поставленные
   * галочки, а снятые: новая строка корзины в этот набор не входит и выбрана
   * сама. Недоступные позиции не выбираются никогда.
   */
  const [unselected, setUnselected] = useState<Set<string>>(new Set());
  const [detailsItem, setDetailsItem] = useState<CartItem | null>(null);
  const items = cart?.items ?? [];
  const commonItems = useMemo(() => items.filter(i => !isMadeToOrder(i)), [items]);
  const orderItems = useMemo(() => items.filter(isMadeToOrder), [items]);
  const tabItems = tab === "common" ? commonItems : orderItems;

  // Выбор действует только над товарами активной вкладки.
  const isSelected = (i_0: CartItem) => i_0.available && !unselected.has(i_0.id);
  const selectedInTab = tabItems.filter(isSelected);
  const selectableInTab = tabItems.filter(i_1 => i_1.available);
  const allSelected = selectableInTab.length > 0 && selectableInTab.every(isSelected);
  const anySelected = selectedInTab.length > 0;

  // Сводка и «К оформлению» — по выбранным товарам. Сняли все галочки —
  // сводки нет, как в кадре 3871:53928. Недоступные позиции в сумму не входят
  // никогда: они остаются в корзине (серыми), но купить их нельзя.
  const summaryItems = selectedInTab;
  const summaryCount = summaryItems.reduce((s_5, i_2) => s_5 + i_2.qty, 0);
  const summarySum = summaryItems.reduce((s_6, i_3) => s_6 + i_3.lineTotal, 0);
  // Скидка промокода — на корзину целиком. Частичный заказ (вкладка, выбор)
  // шлюз оформляет без неё, поэтому и сводка показывает её, только когда
  // оформляется вся доступная корзина: раньше здесь была пропорциональная
  // доля, а в оформлении её уже не было.
  const wholeCart = summaryItems.length === items.filter(i_4 => i_4.available).length;
  const discount = wholeCart ? cart?.discount ?? 0 : 0;
  const total = Math.max(0, summarySum - discount);

  /*
   * Оформление только того, что оформляет покупатель.
   *
   * «Оплатить» у товара под заказ и «К оформлению» во вкладке раньше вели на
   * /checkout без списка — оформление брало всю корзину, и вместе с заказом
   * уезжали товары из соседней вкладки (Айталы 01.10.2026). Теперь в адресе
   * идут строки корзины (`lines`), а шлюз оформляет и чистит после оплаты
   * только их. Если оформляется вся доступная корзина — без параметра: так
   * сохраняется промокод (в частичный заказ шлюз скидку не переносит).
   */
  const checkoutHref = (lineIds: string[]) => {
    const available = items.filter(i_5 => i_5.available);
    const whole = lineIds.length === available.length && available.every(i_6 => lineIds.includes(i_6.id));
    return whole ? "/checkout" : `/checkout?lines=${lineIds.map(encodeURIComponent).join(",")}`;
  };
  // Процент скидки для строки «−N%». Cart несёт только абсолютную сумму —
  // процент выводим из subtotal. Отдельного поля процента в DTO нет.
  // Код действует не на все позиции (fd-promo-15) — процент от всей суммы
  // вводил бы в заблуждение («−5 %» при скидке 15 % на украшения): не рисуем.
  const promoOnAll = summaryItems.every(i_7 => i_7.promoApplied);
  const discountPct = summarySum > 0 && promoOnAll ? Math.round(discount / summarySum * 100) : 0;
  const mutate = async (fn: () => Promise<void>) => {
    setBusy(true);
    try {
      await fn();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t("common.error"));
    } finally {
      setBusy(false);
    }
  };
  const share = async (item: CartItem) => {
    const url = `${window.location.origin}/product/${item.productId}`;
    try {
      if (navigator.share) {
        await navigator.share({
          title: item.name,
          url
        });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success(t("cart.linkCopied", {
          defaultValue: "Ссылка скопирована"
        }));
      }
    } catch {
      /* пользователь отменил share — молча игнорируем */
    }
  };
  const shareSelected = async () => {
    const chosen = selectedInTab;
    if (chosen.length === 0) return;
    if (chosen.length === 1 && chosen[0]) return share(chosen[0]);
    const links = chosen.map(i_8 => `${window.location.origin}/product/${i_8.productId}`).join("\n");
    try {
      await navigator.clipboard.writeText(links);
      toast.success(t("cart.linksCopied", {
        defaultValue: "Ссылки скопированы"
      }));
    } catch {
      /* нет доступа к буферу — молча игнорируем */
    }
  };
  const toggleSelect = (id: string) => setUnselected(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const toggleSelectAll = () => setUnselected(prev_0 => {
    const next_0 = new Set(prev_0);
    if (allSelected) for (const i_9 of selectableInTab) next_0.add(i_9.id);else for (const i_10 of selectableInTab) next_0.delete(i_10.id);
    return next_0;
  });

  /*
   * Удаление выделенного — ОДНИМ запросом.
   *
   * Раньше шло N запросов подряд, и, поскольку каждый ответ заменяет корзину
   * целиком, строки исчезали по очереди на глазах. Плюс подтверждение: в
   * борде такого окна нет, но удаление нескольких позиций необратимо, а
   * «Отменить» в корзине не предусмотрено.
   */
  const removeSelected = () => {
    const ids = selectedInTab.map(i_11 => i_11.id);
    if (ids.length === 0) return;
    if (!window.confirm(t("cart.removeSelectedConfirm", {
      count: ids.length
    }))) return;
    return mutate(async () => {
      await removeMany(ids);
    });
  };
  const moveToWishlist = (item_0: CartItem) => mutate(async () => {
    const liked = toggleProduct(item_0.productId);
    void setProductLikedOnServer(item_0.productId, liked);
    await remove(item_0.id);
  });

  // Снятые галочки переживают переключение вкладок: выбор у каждой вкладки свой.
  const switchTab = (next_1: Tab) => setTab(next_1);
  if (items.length === 0) {
    return <EmptyState icon={ShoppingBag} title={t("cart.empty")} hint={t("cart.emptyHint")} action={<Link to="/catalog" className="inline-flex h-10 items-center rounded-[24px] bg-osmix-tangelo px-6 type-button text-white outline-none transition-colors hover:bg-osmix-tangelo/90 focus-visible:ring-2 focus-visible:ring-osmix-tangelo/40">
						{t("cart.continueShopping")}
					</Link>} />;
  }
  return <div className="mx-auto flex w-full max-w-[1140px] flex-col gap-5">
			{/* Заголовок 20/23 w600, ниже вкладки 16/20 (активная w600) и справа от
       них пилюля «Мои заказы» (114×32 r10 на #989AA2 12 %, 12/16 w600) —
       кадры 3919:16116 / 3919:16693. Между блоками по 20. */}
			<h1 className="text-[20px] leading-[23px] font-semibold tracking-[-0.31px] text-[#2E2E2E]">{t("cart.title")}</h1>
			<div className="flex items-end justify-between gap-4">
				<div className="flex gap-5" role="tablist">
					{([["common", t("cart.tabCommon", {
          defaultValue: "Общая"
        })], ["order", t("cart.tabOrder", {
          defaultValue: "Под заказ"
        })]] as const).map(([key, label]) => <button key={key} type="button" role="tab" aria-selected={tab === key} onClick={() => switchTab(key)} className={cn("relative pb-1 text-[16px] leading-5 tracking-[-0.31px] text-[#1F1F1F] outline-none transition-colors focus-visible:ring-2 focus-visible:ring-osmix-tangelo/40", tab === key ? "font-semibold after:absolute after:inset-x-0 after:bottom-0 after:h-[1.6px] after:bg-[#1F1F1F]" : "font-normal hover:text-[#1F1F1F]/70")}>
							{label}
						</button>)}
				</div>
				<Button asChild variant="gray" size="sm" className="rounded-[10px]">
					<Link to="/profile/orders">
						{t("cart.myOrders", {
            defaultValue: "Мои заказы"
          })}
						<ChevronRight16Icon size={16} />
					</Link>
				</Button>
			</div>

			{/* Панель выбора: выбрать все (14/18) + Удалить/Поделиться (32 r10, 12/16 w600) */}
			<div className="flex flex-wrap items-center gap-2.5">
				<div className="flex select-none items-center gap-2.5">
					<Checkbox id="cart-select-all" shape="round" checked={allSelected ? true : anySelected ? "indeterminate" : false} onCheckedChange={toggleSelectAll} disabled={selectableInTab.length === 0} data-testid="cart-select-all" />
					<label htmlFor="cart-select-all" className="cursor-pointer text-[14px] leading-[18px] text-ds-primary">
						{anySelected ? t("cart.selectedCount", {
            defaultValue: "Выбрано {{count}} товара",
            count: selectedInTab.length
          }) : t("cart.selectAll", {
            defaultValue: "Выбрать все"
          })}
					</label>
				</div>

				<Button variant="gray" size="sm" className="rounded-[10px]" disabled={!anySelected || busy} onClick={removeSelected}>
					<Trash16Icon size={16} />
					{t("cart.remove", {
          defaultValue: "Удалить"
        })}
				</Button>
				<Button variant="gray" size="sm" className="rounded-[10px]" disabled={!anySelected} onClick={shareSelected}>
					<Share16Icon size={16} />
					{t("cart.share", {
          defaultValue: "Поделиться"
        })}
				</Button>
			</div>

			<div className="grid items-start gap-5 lg:grid-cols-[minmax(0,753px)_367px] lg:justify-between">
				{/* Список товаров активной вкладки */}
				{tabItems.length === 0 ? <div className="grid min-h-[240px] place-items-center rounded-[20px] bg-osmix-gray-100 p-8 text-center type-body2 text-osmix-text-2">
						{tab === "order" ? t("cart.orderEmpty", {
          defaultValue: "Здесь появятся товары, изготавливаемые под заказ."
        }) : t("cart.commonEmpty", {
          defaultValue: "В этой вкладке нет товаров."
        })}
					</div> : <ul className="flex flex-col gap-2.5">
						{tabItems.map(item_1 => <CartLine key={item_1.id} item={item_1} tab={tab} busy={busy} selected={isSelected(item_1)} onToggleSelect={() => toggleSelect(item_1.id)} onQty={qty => mutate(() => setQty(item_1.id, qty))} onRemove={() => mutate(() => remove(item_1.id))} onMoveToWishlist={() => moveToWishlist(item_1)} onShare={() => share(item_1)} onDetails={() => setDetailsItem(item_1)} onPay={() => navigate(checkoutHref([item_1.id]))} promoCode={cart?.promoCode} freeDelivery={cart?.promoKind === "free_delivery"} />)}
					</ul>}

				{/* Сводка */}
				{anySelected && <aside className="h-fit min-w-0 lg:sticky lg:top-28" data-testid="cart-summary">
						{/* Сводка 367 r16 #F8F8F8, отступ 10, промежутки 16 (кадр 3919:16116):
          «N товара» 16/18 w600 и сумма 16/22 w700, «Скидка OSMIX» 14 w600/w700
          #FA5C2E, промокод h40 r10 на #1F1F1F 3 %, «Итого» 16/18 w600 и 24/33 w700. */}
						<div className="flex flex-col gap-4 rounded-[16px] bg-[#F8F8F8] p-2.5">
							<div className="flex flex-col gap-2.5">
								<div className="flex items-center justify-between gap-1 text-[#161616]">
									<span className="text-[16px] leading-[18px] font-semibold tracking-[-0.32px]">
										{t("cart.itemsCount", {
                  defaultValue: "{{count}} товара",
                  count: summaryCount
                })}
									</span>
									<span className="text-[16px] leading-[22px] font-bold tracking-[-0.32px]">{fmt(summarySum)}</span>
								</div>

								{/* Скидка платформы — рисуется, когда промо применено (>0). */}
								{discount > 0 && <div className="flex items-center justify-between gap-1 text-[#FA5C2E]">
										<span className="text-[14px] leading-[15px] font-semibold tracking-[-0.28px]">
											{/* Код магазина — скидка магазина, не площадки (fd-promo-03). */}
											{cart?.promoShopId ? t("cart.shopDiscount", {
                  defaultValue: "Скидка магазина"
                }) : t("cart.osmixDiscount", {
                  defaultValue: "Скидка OSMIX"
                })}
										</span>
										<span className="flex items-baseline gap-1">
											<span className="text-[14px] leading-[19px] font-bold tracking-[-0.28px]">{fmt(discount)}</span>
											{discountPct > 0 && <span className="text-[12px] leading-4 font-medium text-[#797979]">-{discountPct}%</span>}
										</span>
									</div>}
							</div>

							{/* fd-promo-05: код на бесплатную доставку — скидка в доставке,
           сумма товаров не меняется. Саму доставку считает оформление. */}
							{wholeCart && cart?.promoKind === "free_delivery" && <div className="flex items-center justify-between gap-1 text-[#FA5C2E]" data-testid="cart-free-delivery">
									<span className="text-[14px] leading-[15px] font-semibold tracking-[-0.28px]">
										{t("cart.delivery", {
                defaultValue: "Доставка"
              })}
									</span>
									<span className="text-[14px] leading-[19px] font-bold tracking-[-0.28px]">
										{t("cart.freeDelivery", {
                defaultValue: "Бесплатно по промокоду"
              })}
									</span>
								</div>}

							{/* Промокод: поле, ошибка текстом сервера, лента карточек (27.10.2026). */}
							<CartPromo wholeCart={wholeCart} />

							<div className="flex items-center justify-between gap-1 text-[#161616]">
								<span className="text-[16px] leading-[18px] font-semibold tracking-[-0.32px]">{t("cart.total", {
                defaultValue: "Итого"
              })}</span>
								<span className="text-[24px] leading-[33px] font-bold tracking-[-0.48px]">{fmt(total)}</span>
							</div>
						</div>

						<Button block disabled={busy || summaryItems.length === 0} onClick={() => navigate(checkoutHref(summaryItems.map(i_12 => i_12.id)))} data-testid="go-checkout" className="mt-2.5">
							<span>К оформлению</span>
							<span>{fmt(total)}</span>
						</Button>
					</aside>}
			</div>

			<OrderDetailsModal item={detailsItem} open={detailsItem != null} onOpenChange={open => {
      if (!open) setDetailsItem(null);
    }} />
		</div>;
}
function CartLine(t0) {
  const $ = _c(136);
  const {
    item,
    tab,
    busy,
    selected,
    onToggleSelect,
    onQty,
    onRemove,
    onMoveToWishlist,
    onShare,
    onDetails,
    onPay,
    promoCode,
    freeDelivery
  } = t0;
  const {
    t
  } = useTranslation();
  const fmt = useFormatPrice();
  const [menuOpen, setMenuOpen] = useState(false);
  const soldOut = !item.available;
  let t1;
  if ($[0] !== item.color || $[1] !== item.size || $[2] !== item.variantLabel) {
    t1 = item.variantLabel ?? [item.size, item.color].filter(Boolean).join(", ");
    $[0] = item.color;
    $[1] = item.size;
    $[2] = item.variantLabel;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const spec = t1;
  let t2;
  if ($[4] !== item.name || $[5] !== spec) {
    t2 = [item.name, spec].filter(Boolean);
    $[4] = item.name;
    $[5] = spec;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const nameLine = t2.join(", ");
  const hasDiscount = item.oldUnitPrice != null && item.oldUnitPrice > item.unitPrice;
  let t3;
  if ($[7] !== hasDiscount || $[8] !== item.oldUnitPrice || $[9] !== item.unitPrice) {
    t3 = hasDiscount ? Math.round((1 - item.unitPrice / (item.oldUnitPrice as number)) * 100) : 0;
    $[7] = hasDiscount;
    $[8] = item.oldUnitPrice;
    $[9] = item.unitPrice;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const percent = t3;
  const lowStock = item.stock <= LOW_STOCK;
  const emphasize = lowStock;
  if (soldOut) {
    let t4;
    if ($[11] !== item.name || $[12] !== item.photo) {
      t4 = item.photo && <img src={item.photo} alt={item.name} className="size-full object-cover" />;
      $[11] = item.name;
      $[12] = item.photo;
      $[13] = t4;
    } else {
      t4 = $[13];
    }
    let t5;
    if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <span className="absolute inset-0 bg-white/50" aria-hidden="true" />;
      $[14] = t5;
    } else {
      t5 = $[14];
    }
    let t6;
    if ($[15] !== t) {
      t6 = t("cart.soldOut", {
        defaultValue: "\u0422\u043E\u0432\u0430\u0440 \u0437\u0430\u043A\u043E\u043D\u0447\u0438\u043B\u0441\u044F"
      });
      $[15] = t;
      $[16] = t6;
    } else {
      t6 = $[16];
    }
    let t7;
    if ($[17] !== t6) {
      t7 = <Checkbox shape="round" checked={false} disabled={true} aria-label={t6} className="absolute top-1.5 left-1.5 border-transparent bg-ds-primary/12 disabled:opacity-100" />;
      $[17] = t6;
      $[18] = t7;
    } else {
      t7 = $[18];
    }
    let t8;
    if ($[19] !== t4 || $[20] !== t7) {
      t8 = <div className="relative h-[133px] w-[100px] shrink-0 overflow-hidden rounded-[16px] bg-white">{t4}{t5}{t7}</div>;
      $[19] = t4;
      $[20] = t7;
      $[21] = t8;
    } else {
      t8 = $[21];
    }
    let t9;
    if ($[22] !== item.unavailableReason || $[23] !== t) {
      t9 = item.unavailableReason && item.unavailableReason !== "out_of_stock" ? t("cart.unavailableForOrder", {
        defaultValue: "\u041D\u0435\u0434\u043E\u0441\u0442\u0443\u043F\u043D\u043E \u0434\u043B\u044F \u0437\u0430\u043A\u0430\u0437\u0430"
      }) : t("cart.soldOut", {
        defaultValue: "\u0422\u043E\u0432\u0430\u0440 \u0437\u0430\u043A\u043E\u043D\u0447\u0438\u043B\u0441\u044F"
      });
      $[22] = item.unavailableReason;
      $[23] = t;
      $[24] = t9;
    } else {
      t9 = $[24];
    }
    let t10;
    if ($[25] !== t9) {
      t10 = <span className="text-[14px] leading-[14px] font-bold tracking-[-0.42px] text-[#333] uppercase">{t9}</span>;
      $[25] = t9;
      $[26] = t10;
    } else {
      t10 = $[26];
    }
    let t11;
    if ($[27] !== item.brand) {
      t11 = <span className="text-[14px] leading-[15px] font-semibold tracking-[-0.28px] text-[#161616]">{item.brand}</span>;
      $[27] = item.brand;
      $[28] = t11;
    } else {
      t11 = $[28];
    }
    let t12;
    if ($[29] !== nameLine) {
      t12 = <span className="text-[12px] leading-4 font-medium tracking-[-0.24px] text-[#797979]">{nameLine}</span>;
      $[29] = nameLine;
      $[30] = t12;
    } else {
      t12 = $[30];
    }
    let t13;
    if ($[31] !== t11 || $[32] !== t12) {
      t13 = <span className="flex flex-col gap-1">{t11}{t12}</span>;
      $[31] = t11;
      $[32] = t12;
      $[33] = t13;
    } else {
      t13 = $[33];
    }
    let t14;
    if ($[34] !== t10 || $[35] !== t13) {
      t14 = <div className="flex min-w-0 flex-1 flex-col gap-2 pr-8">{t10}{t13}</div>;
      $[34] = t10;
      $[35] = t13;
      $[36] = t14;
    } else {
      t14 = $[36];
    }
    let t15;
    if ($[37] !== t) {
      t15 = t("cart.remove", {
        defaultValue: "\u0423\u0434\u0430\u043B\u0438\u0442\u044C"
      });
      $[37] = t;
      $[38] = t15;
    } else {
      t15 = $[38];
    }
    let t16;
    if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
      t16 = <XIcon size={24} />;
      $[39] = t16;
    } else {
      t16 = $[39];
    }
    let t17;
    if ($[40] !== busy || $[41] !== onRemove || $[42] !== t15) {
      t17 = <button type="button" disabled={busy} onClick={onRemove} aria-label={t15} className="absolute top-2 right-2 grid size-6 place-items-center rounded-full text-[#1F1F1F] outline-none transition-colors hover:text-[#1F1F1F]/70 focus-visible:ring-2 focus-visible:ring-osmix-tangelo/40">{t16}</button>;
      $[40] = busy;
      $[41] = onRemove;
      $[42] = t15;
      $[43] = t17;
    } else {
      t17 = $[43];
    }
    let t18;
    if ($[44] !== t14 || $[45] !== t17 || $[46] !== t8) {
      t18 = <li className="relative flex min-h-[149px] gap-3 rounded-[20px] bg-[#F8F8F8] p-2">{t8}{t14}{t17}</li>;
      $[44] = t14;
      $[45] = t17;
      $[46] = t8;
      $[47] = t18;
    } else {
      t18 = $[47];
    }
    return t18;
  }
  const t4 = `/product/${item.productId}`;
  let t5;
  if ($[48] !== item.name || $[49] !== item.photo) {
    t5 = item.photo && <img src={item.photo} alt={item.name} className="size-full object-cover" />;
    $[48] = item.name;
    $[49] = item.photo;
    $[50] = t5;
  } else {
    t5 = $[50];
  }
  let t6;
  if ($[51] !== t4 || $[52] !== t5) {
    t6 = <Link to={t4} className="block h-[133px] w-[100px] overflow-hidden rounded-[16px] bg-white outline-none focus-visible:ring-2 focus-visible:ring-osmix-tangelo/40">{t5}</Link>;
    $[51] = t4;
    $[52] = t5;
    $[53] = t6;
  } else {
    t6 = $[53];
  }
  let t7;
  if ($[54] !== item.name || $[55] !== t) {
    t7 = t("cart.selectItem", {
      defaultValue: "\u0412\u044B\u0431\u0440\u0430\u0442\u044C: {{name}}",
      name: item.name
    });
    $[54] = item.name;
    $[55] = t;
    $[56] = t7;
  } else {
    t7 = $[56];
  }
  let t8;
  if ($[57] !== onToggleSelect || $[58] !== selected || $[59] !== t7) {
    t8 = <Checkbox shape="round" checked={selected} onCheckedChange={onToggleSelect} aria-label={t7} data-testid="cart-line-check" className="absolute top-1.5 left-1.5" />;
    $[57] = onToggleSelect;
    $[58] = selected;
    $[59] = t7;
    $[60] = t8;
  } else {
    t8 = $[60];
  }
  let t9;
  if ($[61] !== t6 || $[62] !== t8) {
    t9 = <div className="relative shrink-0">{t6}{t8}</div>;
    $[61] = t6;
    $[62] = t8;
    $[63] = t9;
  } else {
    t9 = $[63];
  }
  const t10 = tab === "order" ? "pr-7 sm:pr-[120px]" : "pr-[38px]";
  let t11;
  if ($[64] !== t10) {
    t11 = cn("flex min-w-0 flex-1 flex-col", t10);
    $[64] = t10;
    $[65] = t11;
  } else {
    t11 = $[65];
  }
  const t12 = emphasize ? "text-[#FA5C2E]" : "text-[#333]";
  let t13;
  if ($[66] !== t12) {
    t13 = cn("text-[14px] leading-[14px] font-bold tracking-[-0.42px]", t12);
    $[66] = t12;
    $[67] = t13;
  } else {
    t13 = $[67];
  }
  let t14;
  if ($[68] !== fmt || $[69] !== item.unitPrice) {
    t14 = fmt(item.unitPrice);
    $[68] = fmt;
    $[69] = item.unitPrice;
    $[70] = t14;
  } else {
    t14 = $[70];
  }
  let t15;
  if ($[71] !== t13 || $[72] !== t14) {
    t15 = <span className={t13}>{t14}</span>;
    $[71] = t13;
    $[72] = t14;
    $[73] = t15;
  } else {
    t15 = $[73];
  }
  let t16;
  if ($[74] !== emphasize || $[75] !== fmt || $[76] !== hasDiscount || $[77] !== item.oldUnitPrice || $[78] !== percent) {
    t16 = hasDiscount && <><span className="text-[12px] leading-3 font-semibold tracking-[-0.36px] text-[#ABABAB] line-through">{fmt(item.oldUnitPrice as number)}</span>{percent > 0 && <span className={cn("rounded-[6px] px-[3px] pt-px pb-[2px] text-[10px] leading-[10px] font-semibold tracking-[-0.3px] text-white", emphasize ? "bg-[#FA5C2E]" : "bg-[#1F1F1F]")}>-{percent}%</span>}</>;
    $[74] = emphasize;
    $[75] = fmt;
    $[76] = hasDiscount;
    $[77] = item.oldUnitPrice;
    $[78] = percent;
    $[79] = t16;
  } else {
    t16 = $[79];
  }
  let t17;
  if ($[80] !== item.minOrderQty || $[81] !== t) {
    t17 = item.minOrderQty != null && item.minOrderQty > 1 && <span className="whitespace-nowrap rounded-[6px] bg-[#1F1F1F] px-[3px] pt-px pb-[2px] text-[10px] leading-[10px] font-semibold tracking-[-0.3px] text-white" data-testid="cart-line-min-order">{t("product.minOrderBadge", {
        defaultValue: "\u043E\u0442 {{count}} \u0448\u0442.",
        count: item.minOrderQty
      })}</span>;
    $[80] = item.minOrderQty;
    $[81] = t;
    $[82] = t17;
  } else {
    t17 = $[82];
  }
  let t18;
  if ($[83] !== t15 || $[84] !== t16 || $[85] !== t17) {
    t18 = <div className="flex flex-wrap items-center gap-x-1 gap-y-1">{t15}{t16}{t17}</div>;
    $[83] = t15;
    $[84] = t16;
    $[85] = t17;
    $[86] = t18;
  } else {
    t18 = $[86];
  }
  const t19 = `/product/${item.productId}`;
  let t20;
  if ($[87] !== item.brand || $[88] !== t19) {
    t20 = <Link to={t19} className="w-fit rounded-sm text-[14px] leading-[15px] font-semibold tracking-[-0.28px] text-[#161616] outline-none transition-colors hover:text-osmix-tangelo focus-visible:ring-2 focus-visible:ring-osmix-tangelo/40">{item.brand}</Link>;
    $[87] = item.brand;
    $[88] = t19;
    $[89] = t20;
  } else {
    t20 = $[89];
  }
  let t21;
  if ($[90] !== nameLine) {
    t21 = <span className="text-[12px] leading-4 font-medium tracking-[-0.24px] text-[#797979]">{nameLine}</span>;
    $[90] = nameLine;
    $[91] = t21;
  } else {
    t21 = $[91];
  }
  let t22;
  if ($[92] !== t20 || $[93] !== t21) {
    t22 = <span className="flex flex-col gap-1">{t20}{t21}</span>;
    $[92] = t20;
    $[93] = t21;
    $[94] = t22;
  } else {
    t22 = $[94];
  }
  let t23;
  if ($[95] !== item.sellerPromo || $[96] !== t) {
    t23 = item.sellerPromo && <span className="text-[12px] leading-4 font-semibold tracking-[-0.24px] text-[#FA5C2E]" data-testid="cart-line-seller-promo">{t("cart.sellerPromo", {
        defaultValue: "\u0410\u043A\u0446\u0438\u044F \xAB{{title}}\xBB",
        title: item.sellerPromo.title
      })}</span>;
    $[95] = item.sellerPromo;
    $[96] = t;
    $[97] = t23;
  } else {
    t23 = $[97];
  }
  let t24;
  if ($[98] !== fmt || $[99] !== freeDelivery || $[100] !== item.discount || $[101] !== item.promoApplied || $[102] !== promoCode || $[103] !== t) {
    t24 = promoCode && item.promoApplied && <span className="w-fit rounded-[6px] bg-[#FA5C2E]/10 px-1.5 py-0.5 text-[12px] leading-4 font-semibold tracking-[-0.24px] text-[#FA5C2E]" data-testid="cart-line-promo">{item.discount && item.discount > 0 ? t("cart.linePromo", {
        defaultValue: "\u041F\u0440\u043E\u043C\u043E\u043A\u043E\u0434 {{code}}: \u2212{{sum}}",
        code: promoCode,
        sum: fmt(item.discount)
      }) : freeDelivery ? t("cart.lineFreeDelivery", {
        defaultValue: "\u041F\u0440\u043E\u043C\u043E\u043A\u043E\u0434 {{code}}: \u0431\u0435\u0441\u043F\u043B\u0430\u0442\u043D\u0430\u044F \u0434\u043E\u0441\u0442\u0430\u0432\u043A\u0430",
        code: promoCode
      }) : t("cart.linePromoNoDiscount", {
        defaultValue: "\u041F\u0440\u043E\u043C\u043E\u043A\u043E\u0434 {{code}}",
        code: promoCode
      })}</span>;
    $[98] = fmt;
    $[99] = freeDelivery;
    $[100] = item.discount;
    $[101] = item.promoApplied;
    $[102] = promoCode;
    $[103] = t;
    $[104] = t24;
  } else {
    t24 = $[104];
  }
  let t25;
  if ($[105] !== t18 || $[106] !== t22 || $[107] !== t23 || $[108] !== t24) {
    t25 = <div className="flex flex-col gap-2">{t18}{t22}{t23}{t24}</div>;
    $[105] = t18;
    $[106] = t22;
    $[107] = t23;
    $[108] = t24;
    $[109] = t25;
  } else {
    t25 = $[109];
  }
  let t26;
  if ($[110] !== busy || $[111] !== item.minOrderQty || $[112] !== item.qty || $[113] !== item.stock || $[114] !== lowStock || $[115] !== onPay || $[116] !== onQty || $[117] !== t || $[118] !== tab) {
    t26 = tab === "order" ? <Button variant="accent" size="sm" onClick={onPay} disabled={busy} data-testid="cart-line-pay" className="mt-auto w-fit rounded-[12px]">{t("cart.pay", {
        defaultValue: "\u041E\u043F\u043B\u0430\u0442\u0438\u0442\u044C"
      })}</Button> : <div className="mt-auto flex items-center gap-2 pt-3"><div className="flex h-[30px] items-center gap-2.5 rounded-[8px] bg-[#ECECEC] px-2 py-1"><button type="button" disabled={busy || item.qty <= Math.max(1, item.minOrderQty ?? 1)} onClick={() => onQty(item.qty - 1)} aria-label="-" className="grid size-5 place-items-center rounded-[4px] text-[#161616] outline-none transition-colors focus-visible:ring-2 focus-visible:ring-osmix-tangelo/40 disabled:text-[#ABABAB]"><MinusOutlineIcon size={20} /></button><span className="min-w-2 text-center text-[16px] leading-[22px] font-bold tracking-[-0.32px] text-[#161616]">{item.qty}</span><button type="button" disabled={busy || item.qty >= item.stock} onClick={() => onQty(item.qty + 1)} aria-label="+" className="grid size-5 place-items-center rounded-[4px] text-[#161616] outline-none transition-colors focus-visible:ring-2 focus-visible:ring-osmix-tangelo/40 disabled:text-[#ABABAB]"><PlusOutlineIcon size={20} /></button></div><span className={cn("text-[12px] leading-4 font-medium tracking-[-0.24px]", lowStock ? "text-[#E1574F]" : "text-[#797979]")}>{t("cart.inStock", {
          defaultValue: "{{count}} \u0448\u0442. \u0432 \u043D\u0430\u043B\u0438\u0447\u0438\u0438",
          count: item.stock
        })}</span></div>;
    $[110] = busy;
    $[111] = item.minOrderQty;
    $[112] = item.qty;
    $[113] = item.stock;
    $[114] = lowStock;
    $[115] = onPay;
    $[116] = onQty;
    $[117] = t;
    $[118] = tab;
    $[119] = t26;
  } else {
    t26 = $[119];
  }
  let t27;
  if ($[120] !== t11 || $[121] !== t25 || $[122] !== t26) {
    t27 = <div className={t11}>{t25}{t26}</div>;
    $[120] = t11;
    $[121] = t25;
    $[122] = t26;
    $[123] = t27;
  } else {
    t27 = $[123];
  }
  let t28;
  if ($[124] !== menuOpen || $[125] !== onDetails || $[126] !== onMoveToWishlist || $[127] !== onRemove || $[128] !== onShare || $[129] !== t || $[130] !== tab) {
    t28 = tab === "order" ? <button type="button" onClick={onDetails} className="absolute top-2 right-2 inline-flex h-5 items-center gap-1.5 rounded-sm text-[12px] leading-4 font-semibold text-[#B5B5B5] outline-none transition-colors hover:text-[#797979] focus-visible:ring-2 focus-visible:ring-osmix-tangelo/40"><span className="sr-only sm:not-sr-only">{t("cart.orderDetails", {
          defaultValue: "\u0414\u0435\u0442\u0430\u043B\u0438 \u0437\u0430\u043A\u0430\u0437\u0430"
        })}</span><InfoCircle20Icon size={20} /></button> : <Popover open={menuOpen} onOpenChange={setMenuOpen}><PopoverTrigger asChild={true}><button type="button" aria-label={t("cart.moreActions", {
          defaultValue: "\u0414\u0435\u0439\u0441\u0442\u0432\u0438\u044F"
        })} className="absolute top-2 right-2 grid size-[30px] place-items-center rounded-full text-[#ABABAB] outline-none transition-colors hover:bg-white/70 hover:text-[#1F1F1F] data-[state=open]:bg-[#1F1F1F]/[0.04] data-[state=open]:text-[#1F1F1F] focus-visible:ring-2 focus-visible:ring-osmix-tangelo/40"><MoreCircleIcon size={30} /></button></PopoverTrigger><PopoverContent align="end" sideOffset={4} className="w-[192px] rounded-[10px] border-0 p-[3px] shadow-[0_10px_20px_rgba(0,0,0,0.1)]"><MenuItem onClick={() => {
          setMenuOpen(false);
          onMoveToWishlist();
        }}>{t("cart.moveToWishlist", {
            defaultValue: "\u041F\u0435\u0440\u0435\u043D\u0435\u0441\u0442\u0438 \u0432 \u0438\u0437\u0431\u0440\u0430\u043D\u043D\u043E\u0435"
          })}</MenuItem><MenuItem onClick={() => {
          setMenuOpen(false);
          onShare();
        }}>{t("cart.share", {
            defaultValue: "\u041F\u043E\u0434\u0435\u043B\u0438\u0442\u044C\u0441\u044F"
          })}</MenuItem><MenuItem danger={true} onClick={() => {
          setMenuOpen(false);
          onRemove();
        }}>{t("cart.remove", {
            defaultValue: "\u0423\u0434\u0430\u043B\u0438\u0442\u044C"
          })}</MenuItem></PopoverContent></Popover>;
    $[124] = menuOpen;
    $[125] = onDetails;
    $[126] = onMoveToWishlist;
    $[127] = onRemove;
    $[128] = onShare;
    $[129] = t;
    $[130] = tab;
    $[131] = t28;
  } else {
    t28 = $[131];
  }
  let t29;
  if ($[132] !== t27 || $[133] !== t28 || $[134] !== t9) {
    t29 = <li className="relative flex min-h-[149px] gap-3 rounded-[20px] bg-[#F8F8F8] p-2" data-testid="cart-line">{t9}{t27}{t28}</li>;
    $[132] = t27;
    $[133] = t28;
    $[134] = t9;
    $[135] = t29;
  } else {
    t29 = $[135];
  }
  return t29;
}
function MenuItem(t0) {
  const $ = _c(6);
  const {
    children,
    danger,
    onClick
  } = t0;
  const t1 = danger ? "text-[#E1574F]" : "text-[#1F1F1F]";
  let t2;
  if ($[0] !== t1) {
    t2 = cn("flex h-[39px] w-full items-center rounded-[8px] px-3 py-2.5 text-left text-[14px] leading-[19px] font-medium outline-none transition-colors hover:bg-[#1F1F1F]/[0.04] focus-visible:bg-[#1F1F1F]/[0.04]", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== children || $[3] !== onClick || $[4] !== t2) {
    t3 = <button type="button" onClick={onClick} className={t2}>{children}</button>;
    $[2] = children;
    $[3] = onClick;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}